import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Clock, Plus } from 'lucide-react';
import { FlatRateItem, FlatRateCategory, createFlatRateItem, updateFlatRateItem, getFlatRateCategories, createFlatRateCategory } from '../lib/flat-rates';
import { selectInputContentOnFocus } from '../lib/utils';

interface FlatRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  item?: FlatRateItem | null;
  mode: 'create' | 'edit';
}

export default function FlatRateModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  item,
  mode 
}: FlatRateModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<FlatRateCategory[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    hours: '',
    category_id: ''
  });

  const handleAddCategory = async () => {
    if (!businessId || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createFlatRateCategory({
        business_id: businessId,
        name: newCategoryName.trim(),
        description: '',
        order_index: categories.length
      });
      
      await loadCategories();
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    if (!businessId) return;
    
    try {
      const data = await getFlatRateCategories(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      if (mode === 'edit' && item) {
        setFormData({
          name: item.name,
          hours: item.hours.toString(),
          category_id: item.category_id || ''
        });
      } else {
        // Reset form for create mode
        setFormData({
          name: '',
          hours: '',
          category_id: ''
        });
      }
      
      // Auto-focus on name field
      setTimeout(() => {
        const nameInput = document.querySelector('[data-flatrate-name-input]') as HTMLInputElement;
        if (nameInput) {
          nameInput.focus();
        }
      }, 100);
    }
  }, [isOpen, item, mode]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        hours: '',
        category_id: ''
      });
      setError(null);
      setIsAddingCategory(false);
      setNewCategoryName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      setError('Item name is required');
      return;
    }

    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours < 0) {
      setError('Please enter valid hours');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'create') {
        await createFlatRateItem({
          business_id: businessId,
          name: formData.name.trim(),
          description: '',
          category_id: formData.category_id || null,
          hours,
          order_index: 0
        });
      } else if (mode === 'edit' && item) {
        await updateFlatRateItem(item.id, {
          name: formData.name.trim(),
          category_id: formData.category_id || null,
          hours
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving flat rate item:', err);
      setError(err instanceof Error ? err.message : 'Failed to save flat rate item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {mode === 'create' ? 'New Flat Rate Item' : 'Edit Flat Rate Item'}
            </h2>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Item Information */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Item Name *
                    </label>
                    <input
                      type="text"
                      required
                      data-flatrate-name-input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter item name"
                    />
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category
                    </label>
                    {isAddingCategory ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                          placeholder="Enter category name"
                        />
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingCategory(false);
                              setNewCategoryName('');
                            }}
                            className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleAddCategory}
                            disabled={!newCategoryName.trim() || loading}
                            className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {loading ? 'Creating...' : 'Add'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <select
                          value={formData.category_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">No Category</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setIsAddingCategory(true)}
                          className="inline-flex items-center px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-4 w-4 mr-1.5" />
                          New Category
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Hours Information */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hours *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.1"
                      value={formData.hours}
                      onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                      onFocus={selectInputContentOnFocus}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter hours"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`${isMobile ? 'p-4' : 'p-6'} pt-0`}>
              <button
                type="submit"
                disabled={loading || !formData.name.trim() || !formData.hours}
                className="w-full px-4 py-3 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Item' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // Use React portal to render modal at document.body level for proper z-index
  return ReactDOM.createPortal(modalContent, document.body);
}